<script setup lang="ts">
import { ref, computed } from "vue";
import { useEventStore, CalendarEvent, CalendarEventFilter } from "@/ts/stores/event";
import { Spinner } from "@/ts/components/elements";
import { FormInput, FormCombobox } from "@/ts/components/form";
import { EventCard, EventArchivedCard, EventRowCard } from "@/ts/components/events";
import { useDebounceFn } from "@vueuse/core";
import { parse, format } from "fecha";

var slugify = require("slugify");

/**
 * Interfaces
 */
interface Props {
    view?: "list" | "grid";
    full?: boolean;
    archived?: boolean;
    appName: string;
    backgroundFigures?: boolean;
    backgroundFiguresColor?: string;
}

interface EventFilter {
    categoryId: number | null;
}

/**
 * Use functions
 */
const eventStore = useEventStore();

/**
 * Private variables
 */
const props = defineProps<Props>();
const search = ref();
const date = ref();
const page = ref(1);
const per_page = EVENTS_PER_PAGE;
const end_reached = ref(false);
const loading = ref(true);
const debounceWaiting = ref(false);
const filter = ref<EventFilter>({
    categoryId: null,
});

/**
 * Get query filter
 */

const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop: string) => searchParams.get(prop),
});

//@ts-ignore
let category = params.categorie;

if (props.appName === "PLUG-IN") {
    category = "Plug-in";
}

/**
 * Retrieve API data
 */
const promises = [];
promises.push(eventStore.indexCategories(props.archived ? props.archived : false));
promises.push(eventStore.indexMonths(props.archived ? props.archived : false));
Promise.all(promises).then(() => {
    if (category) {
        const matchingCategory = eventStore.categories.find((c) => slugify(c.name) === category);
        if (matchingCategory) {
            filter.value.categoryId = matchingCategory.id;
        }
    }
    eventStore
        .indexEvents({
            ...(props.archived && { archived: true }),
            ...(filter.value.categoryId !== null && { categories: [filter.value.categoryId] }),
        })
        .then(() => {
            loading.value = false;
        });
});

/**
 * Computed variables
 */
const events = computed<CalendarEvent[]>(() => {
    const events = eventStore.all;
    return props.full ? events : events.slice(0, 5);
});

const canLoadMore = computed<boolean | undefined>(() => {
    return props.full && !loading.value && events.value.length > 0 && events.value.length % per_page === 0 && !end_reached.value;
});

const availableMonths = computed<{ value: string; label: string }[]>(() => {
    return eventStore.months.map((month: string) => {
        //const dateObj = parse(month, "YYYY-MM");
        const dateObj = parse(month, "YYYY-MM");
        return { value: month, label: format(dateObj!, "MMMM YYYY") };
    });
});

/**
 * Functions
 */
function toggleFilter(id: number) {
    if (filter.value.categoryId === id) {
        filter.value.categoryId = null;
    } else {
        filter.value.categoryId = id;
    }

    const url = new URL(window.location.href);

    if (filter.value.categoryId) {
        const matchingCategory = eventStore.categories.find((c) => c.id === filter.value.categoryId);
        if (matchingCategory) {
            url.searchParams.set("categorie", slugify(matchingCategory.name));
        }
    } else {
        url.searchParams.delete("categorie");
    }

    window.history.replaceState(null, "", url);

    loadMore(false);
}

function resetPagination() {
    end_reached.value = false;
    page.value = 1;
}

function loadMore(next_page: boolean = true) {
    debounceWaiting.value = true;
    loadMoreDebounced(next_page);
}

const loadMoreDebounced = useDebounceFn((next_page: boolean = true) => {
    debounceWaiting.value = false;

    if (next_page) {
        page.value++;
    } else {
        resetPagination();
    }

    loading.value = true;

    const eventFilter: CalendarEventFilter = {
        ...(filter.value.categoryId !== null && { categories: [filter.value.categoryId] }),
        ...(search.value && search.value.inputValue && search.value.inputValue !== "" && { search: search.value.inputValue }),
        ...(date.value && date.value.value && date.value.value.length > 0 && { dates: date.value.value }),
        ...(props.archived && { archived: true }),
        page: page.value,
    };

    const oldLength = eventStore.all.length;
    eventStore.indexEvents(eventFilter).then(() => {
        const newLength = eventStore.all.length;
        end_reached.value = oldLength === newLength;
        loading.value = false;
    });
}, 1000);
</script>

<template>
    <div class="relative">
        <transition name="fade">
            <div v-if="!loading && appName !== 'PLUG-IN' && eventStore.categories && eventStore.categories.length" class="relative z-10 -m-1 flex flex-wrap md:-m-2">
                <div class="p-1 md:p-2" v-for="category in eventStore.categories.filter((c) => c.slug !== 'geen-categorie' && c.count > 0)">
                    <button class="btn btn-green" :class="{ active: category.id === filter.categoryId }" @click="toggleFilter(category.id)" v-html="category.name"></button>
                </div>
            </div>
        </transition>
        <div v-if="full" class="relative z-20 mt-6 flex flex-col gap-4 md:flex-row lg:mt-10">
            <form-input ref="search" @input="loadMore(false)" class="w-full max-w-[31.25rem]" name="query" type="text" :auto-focus="true" placeholder="Zoeken">
                <template v-slot:prefix><svg-vue icon="search" class="h-5 w-auto"></svg-vue></template>
            </form-input>
            <form-combobox ref="date" name="date" @select="loadMore(false)" placeholder="Datum" :options="availableMonths" class="w-full md:max-w-xs" mode="tags"></form-combobox>
        </div>
        <div class="relative z-10 mt-10 flex flex-col justify-center md:mt-[3.75rem]" :class="{ 'min-h-[20.9375rem]': view === 'list', 'min-h-[25rem]': view === 'grid' }">
            <transition name="fade">
                <p class="mb-6 font-heading text-32 font-[690] uppercase [font-stretch:_190%]" v-if="full && events.length && !loading && !debounceWaiting && search.inputValue">
                    {{ events.length }} {{ events.length > 1 ? "resultaten" : "resultaat" }} voor
                    <span class="text-orange">‘{{ search.inputValue }}’</span>
                </p>
            </transition>
            <transition name="fade" tag="div" class="justify-self-center">
                <p class="self-center text-center font-heading text-32 font-[690] uppercase [font-stretch:_190%]" v-if="!loading && events.length === 0">Geen evenementen gevonden</p>
            </transition>
            <!-- list view -->
            <transition v-if="!archived" name="fade">
                <div v-if="view === 'list' && (!loading || (loading && page > 1))">
                    <transition-group tag="div" class="relative mb-10 flex flex-col md:mb-[3.75rem]" name="fade">
                        <template v-for="event in events" :key="event.post_name">
                            <hr class="mb-6 text-green" />
                            <event-row-card :event="event" :appName="appName"></event-row-card>
                        </template>
                    </transition-group>
                </div>
            </transition>
            <!-- grid view -->
            <transition v-if="!archived" name="fade">
                <div v-if="view === 'grid' && (!loading || (loading && page > 1))" class="relative mb-10 md:mb-[3.75rem]">
                    <transition-group class="-m-4 flex flex-wrap" name="fade" tag="div">
                        <div class="flex w-full flex-col p-4 md:w-1/3 lg:w-1/3" v-for="(event, index) in events" :key="event.post_name">
                            <event-card :event="event" :appName="appName"></event-card>
                            <transition name="fade">
                                <hr v-if="(events.length % 2 === 0 && index < events.length - 2) || (events.length % 2 !== 0 && index < events.length - 1)" class="mt-10 mb-4 text-green" />
                            </transition>
                        </div>
                    </transition-group>
                </div>
            </transition>
            <!-- archived view -->
            <transition v-if="archived" name="fade">
                <div v-if="!loading || (loading && page > 1)" class="relative mb-10 md:mb-[3.75rem]">
                    <transition-group class="-m-4 flex flex-wrap" name="fade" tag="div">
                        <div class="flex w-full flex-col p-4 md:w-1/2 xl:w-1/4" v-for="(event, index) in events" :key="event.post_name">
                            <event-archived-card :event="event"></event-archived-card>
                            <transition name="fade">
                                <hr v-if="(events.length % 3 === 0 && index < events.length - 4) || (events.length % 3 !== 0 && index < events.length - 2)" class="mt-10 mb-4 text-green" />
                            </transition>
                        </div>
                    </transition-group>
                </div>
            </transition>
            <transition-group name="fade-absolute" mode="out-in" tag="div" class="relative flex h-0 w-full justify-center overflow-hidden transition-all" :class="{ '!h-14': canLoadMore || loading }">
                <div v-if="canLoadMore" class="relative flex md:justify-center">
                    <button @click="loadMore()" class="btn btn-black">
                        Meer laden
                        <svg-vue icon="arrow" class="ml-[.625rem] h-6 w-auto"></svg-vue>
                    </button>
                </div>
                <template v-if="loading">
                    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <spinner></spinner>
                    </div>
                </template>
            </transition-group>
        </div>
        <div v-if="!full" class="relative z-10 flex md:justify-center">
            <a :href="appName === 'PLUG-IN' ? 'https://hajee.be/kalender?categorie=Plug-in' : '/kalender'" :target="appName === 'PLUG-IN' ? '_blank' : undefined" class="btn btn-green text-white">
                Bekijk alle
                <svg-vue icon="arrow" class="ml-[.625rem] h-6 w-auto"></svg-vue>
            </a>
        </div>
        <!-- background icons -->
        <template v-if="backgroundFigures">
            <svg-vue icon="star" class="absolute top-4 right-[3.75rem] hidden h-80 w-auto select-none lg:inline" :class="backgroundFiguresColor"></svg-vue>
            <svg-vue v-if="events.length" icon="flowerone" class="absolute top-[30.25rem] left-[-5.875rem] hidden h-[19rem] w-auto select-none lg:inline" :class="backgroundFiguresColor"></svg-vue>
            <svg-vue v-if="events.length && events.length > 3" icon="draw" class="absolute top-[61.5625rem] right-48 hidden h-[14.25rem] w-auto select-none lg:inline" :class="backgroundFiguresColor"></svg-vue>
        </template>
    </div>
</template>
